import React, { useEffect, useState } from 'react';
import { Modal } from '../../UI/Modal';
import { postService } from '../../../services/postService';
import {
    extractPostIdFromInput,
    mapWorkDetailToExampleWork,
    resolveExternalExampleWorkPreview
} from './exampleWorkHelpers';

interface EditableExampleWork {
    type?: 'video' | 'image';
    thumbnailUrl?: string;
    videoUrl?: string;
    images?: string[];
    title: string;
    description?: string;
    sourceUrl?: string;
}

interface ExampleWorkEditModalProps {
    isOpen: boolean;
    work: EditableExampleWork | null;
    onClose: () => void;
    onSave: (patch: {
        title: string;
        description?: string;
        sourceUrl?: string;
        type?: 'video' | 'image';
        thumbnailUrl?: string;
        videoUrl?: string;
        images?: string[];
    }) => void | Promise<void>;
    zIndex?: number;
}

const ExampleWorkEditModal: React.FC<ExampleWorkEditModalProps> = ({
    isOpen,
    work,
    onClose,
    onSave,
    zIndex = 85
}) => {
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [sourceUrl, setSourceUrl] = useState('');
    const [showPreview, setShowPreview] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!isOpen || !work) {
            return;
        }

        setTitle(work.title || '');
        setDescription(work.description || '');
        setSourceUrl(work.sourceUrl || '');
        setShowPreview(Boolean(work.sourceUrl && (work.thumbnailUrl || work.videoUrl || work.images?.length)));
        setError('');
        setIsSaving(false);
    }, [isOpen, work]);

    if (!isOpen || !work) {
        return null;
    }

    const isUrlWork = Boolean(work.sourceUrl);
    const canSave = title.trim().length > 0 && (!isUrlWork || sourceUrl.trim().length > 0) && !isSaving;

    const handleSave = async () => {
        const nextTitle = title.trim();
        const nextDescription = description.trim();

        if (!nextTitle) {
            return;
        }

        setIsSaving(true);
        setError('');

        try {
            if (!isUrlWork) {
                await onSave({
                    title: nextTitle,
                    description: nextDescription
                });
                return;
            }

            const trimmedUrl = sourceUrl.trim();
            if (!trimmedUrl) {
                throw new Error('请输入有效的 URL');
            }

            const postId = extractPostIdFromInput(trimmedUrl);
            if (postId) {
                const detail = await postService.getPostDetail(postId);
                if (!detail) {
                    throw new Error('未找到该本站作品，请检查链接是否正确');
                }

                const mapped = mapWorkDetailToExampleWork(detail);
                await onSave({
                    title: nextTitle,
                    description: nextDescription,
                    sourceUrl: trimmedUrl,
                    type: mapped.type,
                    thumbnailUrl: mapped.thumbnailUrl,
                    videoUrl: mapped.videoUrl,
                    images: mapped.images
                });
                return;
            }

            try {
                new URL(trimmedUrl);
            } catch {
                throw new Error('请输入有效的 URL');
            }

            const preview = showPreview
                ? await resolveExternalExampleWorkPreview(trimmedUrl)
                : {
                    type: 'image' as const,
                    thumbnailUrl: '',
                    videoUrl: undefined,
                    images: undefined
                };

            await onSave({
                title: nextTitle,
                description: nextDescription,
                sourceUrl: trimmedUrl,
                type: preview.type,
                thumbnailUrl: preview.thumbnailUrl,
                videoUrl: preview.videoUrl,
                images: preview.images
            });
        } catch (saveError: any) {
            setError(saveError?.message || '保存示例作品失败');
            setIsSaving(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="编辑示例作品"
            size="md"
            zIndex={zIndex}
            footer={(
                <>
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 rounded-full text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                    >
                        取消
                    </button>
                    <button
                        type="button"
                        disabled={!canSave}
                        onClick={() => void handleSave()}
                        className={`px-5 py-2 rounded-full text-sm font-medium text-white transition-colors ${canSave ? 'bg-black hover:bg-gray-900' : 'bg-gray-300 cursor-not-allowed'}`}
                    >
                        {isSaving ? '保存中...' : '保存'}
                    </button>
                </>
            )}
        >
            <div className="space-y-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">标题</label>
                    <input
                        type="text"
                        value={title}
                        onChange={(event) => setTitle(event.target.value)}
                        placeholder="请输入示例作品标题"
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/10 focus:border-blue-400"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">描述</label>
                    <textarea
                        value={description}
                        onChange={(event) => setDescription(event.target.value)}
                        placeholder="补充这条示例作品的说明"
                        rows={4}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/10 focus:border-blue-400 resize-none"
                    />
                </div>

                {isUrlWork && (
                    <>
                        <div>
                            <div className="mb-2 flex items-center justify-between gap-4">
                                <label className="block text-sm font-medium text-gray-700">参考链接</label>
                                <button
                                    type="button"
                                    aria-pressed={showPreview}
                                    onClick={() => setShowPreview((prev) => !prev)}
                                    className="inline-flex items-center gap-3 rounded-full px-1 py-1 text-sm text-gray-600 transition-colors hover:bg-gray-50"
                                >
                                    <span className="text-sm font-medium text-gray-600">显示预览</span>
                                    <span className={`inline-flex h-6 w-11 shrink-0 items-center rounded-full p-0.5 transition-colors ${showPreview ? 'justify-end bg-blue-500' : 'justify-start bg-gray-300'}`}>
                                        <span className="h-5 w-5 rounded-full bg-white shadow-sm" />
                                    </span>
                                </button>
                            </div>
                            <input
                                type="url"
                                value={sourceUrl}
                                onChange={(event) => setSourceUrl(event.target.value)}
                                placeholder="输入本站作品链接，或任意外站 URL"
                                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/10 focus:border-blue-400"
                            />
                            <p className="mt-2 text-xs text-gray-500">
                                修改链接后会在保存时重新识别；关闭“显示预览”后将只保留文字链接。
                            </p>
                        </div>

                        <div className="rounded-xl border border-blue-100 bg-blue-50/70 px-4 py-3">
                            <p className="text-[11px] font-bold uppercase tracking-wider text-blue-500 mb-1">当前链接</p>
                            <a
                                href={sourceUrl || work.sourceUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="text-sm text-blue-700 break-all hover:underline"
                            >
                                {sourceUrl || work.sourceUrl}
                            </a>
                        </div>
                    </>
                )}

                {error && (
                    <div className="rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">
                        {error}
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default ExampleWorkEditModal;
